import { US_TYPES } from '../usCodegen';
import { orderingPreset } from './orderingPresets';
import { catalogFor } from './orderingModel';
import { applyVariant, hasHousing, txt } from './model';

// Which decoded fields belong in the PRODUCT DATA / HOUSING DATA table.
// Identity fields (mounting, family, size) go into the title instead.
const DATA_KEYS = {
  downlight: ['E', 'W', 'CR', 'CT', 'BM', 'F1', 'F2', 'A', 'D'],
  track: ['P', 'W', 'CR', 'CT', 'BM', 'F1', 'F2', 'V'],
  housing: ['P', 'Z', 'Y', 'H', 'C', 'L', 'W', 'D', 'V', 'A'],
};
const PRODUCT_TYPE = {
  downlight: 'RECESSED DOWNLIGHT',
  track: 'TRACK FITTING',
  housing: 'RECESSED DOWNLIGHT · HOUSING & DRIVER',
};

const fieldLabel = (typeKey, key) => {
  const f = US_TYPES[typeKey].fields.find(x => x.key === key);
  return (f ? f.label.replace(/\s*\(.*?\)\s*/g, '') : key).toUpperCase();
};
const optionLabel = (typeKey, key, code) => {
  const f = US_TYPES[typeKey].fields.find(x => x.key === key);
  return ((f && f.options.find(o => o.code === code)) || {}).label || code;
};

// Point the ordering matrix at the codes the builder produced.
function orderingFromSelection(ordering, typeKey, sel) {
  const kind = typeKey === 'downlight' ? 'downlight' : typeKey;
  const base = ordering.typeKey === kind
    ? ordering
    : { ...orderingPreset(kind), accessories: ordering.accessories };
  const groups = (base.groups || []).map(g => {
    const cols = g.columns || [];
    const keys = cols.map(c => c.id).filter(id => sel[id] != null);
    if (!cols.length || !keys.length) return g;
    const chosen = cols.every(c => !txt(sel[c.id]));
    return {
      ...g,
      enabled: chosen ? false : g.enabled !== false,
      columns: cols.map(c => {
        const code = sel[c.id];
        if (!txt(code)) return c;
        const padded = (c.options || []).some(o => o.code === code)
          ? code
          : ((c.options || []).find(o => Number(o.code) === Number(code)) || {}).code || code;
        const options = (c.options || []).some(o => o.code === padded)
          ? c.options
          : [...(c.options || []), { code: padded, label: optionLabel(typeKey, c.id, code), enabled: true }];
        return { ...c, selected: padded, options };
      }),
    };
  });
  return { ...base, groups };
}

// Merge decoded values into a label/value table, keeping any manual rows.
function mergeRows(rows, pairs) {
  const out = (rows || []).map(r => ({ ...r }));
  pairs.forEach(([label, value]) => {
    const hit = out.find(r => txt(r.label).toUpperCase() === label);
    if (hit) hit.value = value;
    else out.push({ label, value });
  });
  return out;
}

/**
 * Applies a US Smart Code Builder result to the whole datasheet: ordering
 * selections, catalogue number, product data rows and the header fields.
 * Returns { us, filled } where filled is a short human summary.
 */
export function applyCodeToSheet(us, { code, typeKey, sel = {} }) {
  const part = typeKey === 'housing' ? 'housing' : 'fixture';
  let next = us;
  if (typeKey === 'track' && us.variant !== 'track') next = applyVariant(us, 'track');
  else if (typeKey === 'downlight' && us.variant === 'track') next = applyVariant(us, 'fixture');
  else if (typeKey === 'housing' && !hasHousing(us.variant)) next = applyVariant(us, 'housing');

  const data = next[part];
  const ordering = orderingFromSelection(data.ordering, typeKey, sel);
  const generated = catalogFor(ordering, part);
  const pairs = (DATA_KEYS[typeKey] || [])
    .filter(k => txt(sel[k]))
    .map(k => [fieldLabel(typeKey, k), optionLabel(typeKey, k, sel[k])]);

  const table = part === 'housing' ? 'housingData' : 'productData';
  const filledPart = {
    ...data,
    ordering,
    catalogNumber: code,
    // Keep the builder's exact code on the sheet when the matrix can't rebuild it.
    catalogNumberOverride: generated === code ? null : code,
    [table]: { ...data[table], rows: mergeRows(data[table].rows, pairs) },
  };
  const meta = { ...next.meta };
  if (!txt(meta.productType)) meta.productType = PRODUCT_TYPE[typeKey] || '';
  if (!txt(meta.family) && txt(sel.F)) meta.family = optionLabel(typeKey, 'F', sel.F).toUpperCase();
  if (!txt(meta.productName) && txt(sel.F)) {
    meta.productName = [optionLabel(typeKey, 'F', sel.F), txt(sel.Z) && optionLabel(typeKey, 'Z', sel.Z)]
      .filter(Boolean).join(' ').toUpperCase();
  }
  if (part === 'housing' && !txt(filledPart.name)) filledPart.name = txt(meta.productName) || 'HOUSING';

  return {
    us: { ...next, meta, [part]: filledPart },
    filled: `${pairs.length} spec row(s) + ordering selections`,
  };
}
